import { Injectable, computed, inject, signal } from '@angular/core';
import {
  ApiInfoSchema,
  LEGACY_API_INFO,
  supportsCurrentProtocol,
} from '@andor/contracts/compatibility';
import type { ApiInfo } from '@andor/contracts/compatibility';
import { config } from '../../../environments/environment';
import { tenantHeaders } from '../../shared/helpers/tenant';
import { MaintenanceService } from './maintenance.service';

export class IncompatibleApiError extends Error {
  constructor() {
    super('This app needs an update to work with the server.');
  }
}

@Injectable({ providedIn: 'root' })
export class ApiCompatibilityService {
  private readonly maintenance = inject(MaintenanceService);
  readonly info = signal<ApiInfo | null>(null);
  readonly unsupported = computed(() => {
    const info = this.info();
    return info !== null && !supportsCurrentProtocol(info);
  });
  private observation = 0;

  hasCapability(capability: string): boolean {
    return !this.unsupported() && (this.info()?.capabilities.includes(capability) ?? false);
  }

  /** Independent of intercepted HTTP, like the maintenance availability probe. */
  async check(signal?: AbortSignal): Promise<void> {
    const observation = ++this.observation;
    const maintenanceRevision = this.maintenance.revision;
    try {
      const response = await fetch(`${config.backendUrl.replace(/\/$/, '')}/api-info`, {
        cache: 'no-store',
        headers: tenantHeaders(),
        signal: signal
          ? AbortSignal.any([signal, AbortSignal.timeout(5000)])
          : AbortSignal.timeout(5000),
      });
      const info =
        response.status === 404
          ? LEGACY_API_INFO
          : response.ok
            ? ApiInfoSchema.parse(await response.json())
            : null;
      if (!info) throw new Error('API compatibility information unavailable');
      if (signal?.aborted || observation !== this.observation)
        throw new DOMException('Canceled', 'AbortError');
      this.info.set(info);
      if (!supportsCurrentProtocol(info)) throw new IncompatibleApiError();
    } catch (error) {
      if (
        !(error instanceof IncompatibleApiError) &&
        !signal?.aborted &&
        observation === this.observation
      )
        this.maintenance.markUnavailable(maintenanceRevision);
      throw error;
    }
  }
}
